import { useEffect, useRef, useState, type ChangeEvent, type FocusEvent, type FormEvent } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast, { Toaster } from "react-hot-toast";
// import PropTypes from 'prop-types';
// import Select from 'react-select';
// import { baseURL } from '../dataProvider/configData';
import { LANGUAGES } from "../constants";
import logoImage from "../components/images/logo.png";
import { useAuth } from "../app/providers/AuthContext";
import { CircleSpinner } from "../components/Loaders";
import CustomSimpleKeyboard from "../components/CustomSimpleKeyboard";
import { PlusSquareIcon } from "../components/svgIcons/AllIcons";
import { sendAxiosRequestWithRetry } from "../services/sendAxiosRequestWithRetry";
import type { RootState } from "../store";
import { getApiErrorText } from "../shared/api/errors";

import bannerImage from "../components/Authentication/image_waiter_processing.jpg";

import "../components/Authentication/Login.scss";


type KeyboardHandle = {
    setInput: (value: string) => void;
};

type LanguageOption = {
    label: string;
    value: string;
    code: string;
};

const AUTH_ERROR_SEARCH_PARAM = "auth_err";
const AUTH_FAILED_TOAST_ID = "auth-failed-toast";

const getSearchParamsWithoutAuthError = () => {
    const nextSearchParams = new URLSearchParams(window.location.search);
    nextSearchParams.delete(AUTH_ERROR_SEARCH_PARAM);
    return nextSearchParams;
};

// const loginUser = async (credentials) => {
//     return fetch(`${baseURL}/auth/login`, {
//         method: 'POST',
//         headers: {
//             'Content-Type': 'application/json'
//         },
//         body: JSON.stringify(credentials)
//     })
//     .then(data => data.json())
// }


const Login = () => {
    const navigate = useNavigate();

    const { i18n, t } = useTranslation();

    const [searchParams, setSearchParams] = useSearchParams();

    const {
        login
    } = useAuth();

    const isMobileView = useSelector((state: RootState) => state.appGeneral.isMobileView);
    // const showKeyboard = useSelector((state: RootState) => state.appGeneral.showKeyboard);

    const keyboard = useRef<KeyboardHandle | null>(null);
    const usernameInputRef = useRef<HTMLInputElement | null>(null);
    const passwordInputRef = useRef<HTMLInputElement | null>(null);

    const [formIsSubmitting, setFormIsSubmitting] = useState(false);
    const [selectedLanguageValue, setSelectedLanguageValue] = useState((LANGUAGES as LanguageOption[])[0]?.value || "en");
    const [username, setUserName] = useState("");
    const [password, setPassword] = useState("");
    const [errText, setErrText] = useState("");
    const [activeInput, setActiveInput] = useState<HTMLInputElement | null>(null);
    const [showPasswordText, setShowPasswordText] = useState(false);
    const [keyboardLayoutName, setKeyboardLayoutName] = useState("mobile");
    // const [keyboardLayoutName, setKeyboardLayoutName] = useState("numbers");
    // const [submitedAtleastOnce, setSubmitedAtleastOnce] = useState(false);
    // const [waiterUserId, setWaiterUserId] = useState("");
    // const [pinCode, setPinCode] = useState("");

    // const waiters_l = [
    //     // { label: "Choose your username", value: "" },
    //     { label: "Waiter A", value: "waiter_a" },
    //     { label: "Waiter B", value: "waiter_b" },
    // ];

    // const waiterPinCodes_mockArr = {
    //     "waiter_a": 1234,
    //     "waiter_b": 5678
    // };

    const initialLanguageCode = localStorage.getItem("langCode") || 'en';

    useEffect(() => {
        i18n.changeLanguage(initialLanguageCode);
        setSelectedLanguageValue(initialLanguageCode);

        const searchParams_authErr = searchParams.get(AUTH_ERROR_SEARCH_PARAM);
        if (searchParams_authErr === "true") {
            setSearchParams(getSearchParamsWithoutAuthError(), { replace: true });
            toast.error(t("yourAuthenticationFailed") + "!", { id: AUTH_FAILED_TOAST_ID, duration: 8000 });
        }

        return () => {
            toast.dismiss(AUTH_FAILED_TOAST_ID);
        }
    }, []);

    // useEffect(() => {
    //     // document.body.style.backgroundColor = "rgba(90, 115, 211, 0.9)";
    //     document.body.style.backgroundColor = "rgba(6, 98, 178, 0.9)";
    //     return () => {
    //         document.body.style.backgroundColor = "";
    //     }
    // }, []);


    const changeLanguage = (lang: LanguageOption) => {
        const lang_code = lang?.value || "en";

        setSelectedLanguageValue(lang_code);

        localStorage.setItem('langCode', lang_code);
        i18n.changeLanguage(lang_code);

        // window.location.reload(false);
    };

    const onLoginSuccess = (token: string) => {
        login(token);
        const currSP_str = getSearchParamsWithoutAuthError().toString();
        navigate(`/${currSP_str ? "?" + currSP_str : ""}`, { replace: true });
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        setFormIsSubmitting(true);

        const {
            data: loginRespData,
            error: loginRespError
        } = await sendAxiosRequestWithRetry<CashBoxApiDataFor<"/auth/login">>({
            method: "post",
            url: "/auth/login",
            data: {
                username,
                password
            }
        });

        if (loginRespData) {
            const token = loginRespData.access_token;
            onLoginSuccess(token);
        }
        else if (loginRespError) {
            setErrText(getApiErrorText(loginRespError));
        }

        setTimeout(() => {
            setFormIsSubmitting(false);
        }, 0);

        // if (!submitedAtleastOnce) {
        //     setSubmitedAtleastOnce(true);
        // }

        // if (+waiterPinCodes_mockArr[waiterUserId] === +pinCode) {
        //     setWaiterIsLogedIn(true);
        // }
        // else {
        //     setErrText("Your pin is incorrect");
        // }
    };

    const handleChangeUsername = (event: ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        setUserName(inputValue);

        // if (showKeyboard) {
            keyboard.current?.setInput(inputValue);
        // }
    };

    const handleChangePassword = (event: ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        setPassword(inputValue);

        // if (showKeyboard) {
            keyboard.current?.setInput(inputValue);
        // }
    };

    // const handleChangeWaiter = (newValue, actionMeta) => {
    //     const lang_code = newValue?.value;
    //     setWaiterUserId(lang_code);
    // };

    // const handleChangePin = (e) => {
    //     const pinValue = e.target.value;
    //     setPinCode(pinValue);

    //     if (submitedAtleastOnce) {
    //         if (pinValue !== '') {
    //             setErrText("");
    //         }
    //         else {
    //             setErrText("Your pin is incorrect");
    //         }
    //     }
    // };

    const handleFocusOnInput = (event: FocusEvent<HTMLInputElement>) => {
        const inputElem = event.currentTarget;
        setActiveInput(inputElem);

        // if (showKeyboard) {
            keyboard.current?.setInput(inputElem.value);
        // }
    };

    // const handleBlurInput = (e) => {
    // };

    const handleChangeKeyboard = (inputVal: string) => {
        if (activeInput === usernameInputRef.current) {
            const newUsernameValue = inputVal;
            setUserName(newUsernameValue);
        }
        else if (activeInput === passwordInputRef.current) {
            const newPasswordValue = inputVal;
            setPassword(newPasswordValue);
        }

        // setTimeout(() => {
        //     console.log(activeInput);
        //     activeInput?.focus();
        // }, 0);
    };

    const handleKeyboardKeyPress = (key: string) => {
        if (!activeInput) {
            return;
        }
        
        if (key === "{backspace}") {
            if (activeInput === usernameInputRef.current) {
                const newUsernameValue = username?.toString().slice(0, -1);
                setUserName(newUsernameValue);
            }
            else if (activeInput === passwordInputRef.current) {
                const newPasswordValue = password?.toString().slice(0, -1);
                setPassword(newPasswordValue);
            }
        }
        else if (key === "{enter}") {
            if (activeInput === usernameInputRef.current) {
                setActiveInput(passwordInputRef.current);
            }
            else if (activeInput === passwordInputRef.current) {
                setActiveInput(usernameInputRef.current);
            }
        }
        else if (key === "{shift}") {
            if (keyboardLayoutName === "mobile") {
                setKeyboardLayoutName("mobileShift");
            }
            else if (keyboardLayoutName === "mobileShift") {
                setKeyboardLayoutName("mobile");
            }
        }
        else if (key === "{numbers}") {
            setKeyboardLayoutName("numbers");
        }
        else if (key === "{mobileKeyboard}") {
            setKeyboardLayoutName("mobile");
        }
    };


    return (
        <>
            <Toaster/>

            <div className="login-page-wrapper">
                <div className="login-box-wrapper d-flex mt-4 mb-5">
                    <div className="login-box-content card d-flex flex-column align-items-center">
                        <span className="languages-select-box">
                            {LANGUAGES?.length && LANGUAGES.map(lang => (
                                <span
                                    key={lang.value}
                                    className={`lang-item ${selectedLanguageValue === lang.value ? 'active' : ''}`}
                                    onClick={() => changeLanguage(lang)}
                                >
                                    {lang.value}
                                </span>
                            ))}
                        </span>
                        <div>
                            <div className="mt-3 mb-4">
                                <img
                                    src={logoImage}
                                    className="logo-img"
                                    width="267"
                                    height="257"
                                    alt={String(t("appLogoAlt"))}
                                />
                            </div>
                            <h4
                                className="card-title mb-5"
                            >
                                {t("digiAirMenuWaiterLogin")}
                            </h4>
                        </div>
                        <form
                            className="d-flex flex-column flex-grow-1 w-100 mt-2 px-2"
                            onSubmit={handleSubmit}
                            // autoComplete={showKeyboard ? "off" : ""}
                        >
                            {/* {showKeyboard && (
                                <input autocomplete="false" name="hidden" type="text" style={{display: "none"}} />
                            )} */}
                            <div className="d-flex flex-column text-start flex-grow-1">
                                {errText && (
                                    <div className="text-red mb-2 px-1">
                                        {/* Invalid email or password. */}
                                        <span className="fs-4">&bull;</span>
                                        <span className="mt-1 ms-1">{errText}</span>
                                    </div>
                                )}

                                <div className="mb-3">
                                    <label className="form-label ps-1">
                                        {t("username")}
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control text-left ${activeInput === usernameInputRef.current ? 'focused' : ''}`}
                                        ref={usernameInputRef}
                                        value={username}
                                        onChange={handleChangeUsername}
                                        onFocus={handleFocusOnInput}
                                    />

                                    {/* <div className="select2-elem-wrapper">
                                        <Select
                                            // id=""
                                            // title="username"
                                            name="userId"
                                            classNamePrefix="select"
                                            // className={`basic-single custom-select__menu-scrollbar`}
                                            // className="form-control"
                                            // styles={}
                                            options={waiters_l}
                                            defaultValue={waiters_l[0]}
                                            onChange={handleChangeWaiter}
                                            value={waiters_l.find(waiter => waiter.value === waiterUserId) || ''}
                                            // formatOptionLabel={handleFormatOptionLabel}
                                            // menuPortalTarget={document.body}
                                            // isDisabled={false}
                                            // isLoading={false}
                                            isClearable={false}
                                            isSearchable={false}
                                            // menuPlacement="bottom"
                                            placeholder="Choose your username"
                                        />
                                    </div> */}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label ps-1">
                                        {t("password")}
                                        {/* {t("pinCode")} */}
                                    </label>
                                    <div className={`toggleable-password ${showPasswordText ? 'show-password' : ''}`}>
                                        <div className='form-control-wrapper'>
                                            <input
                                                type={`${showPasswordText ? 'text' : 'password'}`}
                                                name="pin-code"
                                                className={`form-control password ${activeInput === passwordInputRef.current ? 'focused' : ''}`}
                                                ref={passwordInputRef}
                                                value={password}
                                                onInput={handleChangePassword}
                                                onFocus={handleFocusOnInput}
                                                // disabled={!waiterUserId}
                                            />
                                            <span
                                                className='eye-btn'
                                                onClick={() => setShowPasswordText(!showPasswordText)}
                                            ></span>
                                            {/* {errText && (
                                                <div className="text-red fst-italic mt-1 px-1">
                                                    { errText }
                                                </div>
                                            )} */}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* <div className="text-end mb-5">
                                <a href="#" className="text-underline">
                                    Forget your PIN ?
                                </a>
                            </div> */}
                            <div className="mt-4 mb-4">
                                <button
                                    type="submit"
                                    className="btn btn-lg submit-btn btn-success px-5 rounded-3 text-uppercase"
                                    // disabled={!waiterUserId || !pinCode}
                                    disabled={formIsSubmitting}
                                >
                                    {formIsSubmitting && (
                                        <span className="icon btn-icon">
                                            <CircleSpinner
                                                spinnerColor="#fff"
                                                circleOpacity={0}
                                                thickness={5}
                                            />
                                        </span>
                                    )}
                                    <span className="btn-text px-3">
                                        {t("login")}
                                        {/* Continue */}
                                    </span>
                                </button>
                            </div>
                        </form>
                    </div>
                    {/* <div className={`login-box-banner ${showKeyboard ? 'banner-with-keyboard' : ''}`}> */}
                    <div className="login-box-banner banner-with-keyboard">
                        <div className="banner-image">
                            <img
                                src={bannerImage}
                                className=""
                                width="800"
                                height="600"
                                alt=""
                            />
                        </div>

                        {/* {!isMobileView && showKeyboard &&( */}
                        {!isMobileView &&(
                            <CustomSimpleKeyboard
                                keyboardRef={r => (keyboard.current = r)}
                                layoutName={keyboardLayoutName}
                                theme={`hg-theme-default hg-layout-default ${keyboardLayoutName === "numbers" ? 'simple-numbers-keyboard' : ''}`}
                                // enterButtonText="Next"
                                // enterButtonText={`${activeInput !== passwordInputRef.current ? 'Next >' : 'Previous'}`}
                                enterButtonText={`${activeInput !== passwordInputRef.current ? t('next') : t('previous')}`}
                                onChange={handleChangeKeyboard}
                                onKeyPress={handleKeyboardKeyPress}
                                // mergeDisplay={true}
                                // autoUseTouchEvents={true}
                                // useButtonTag={true}
                                disabled={!activeInput}
                            />
                        )}
                    </div>

                    <button
                        className="pwa-install-prompt-btn btn btn-secondary d-none"
                        title={String(t("addDAMToHomeScreen"))}
                    >
                        <span className="icon">
                            <PlusSquareIcon />
                        </span>
                        <span className="btn-text-wrapper">
                            <span className="btn-text">
                                {t("installDAM")}
                            </span>
                        </span>
                    </button>
                </div>
                
                {/* {isMobileView && showKeyboard &&( */}
                {isMobileView &&(
                    <CustomSimpleKeyboard
                        keyboardRef={r => (keyboard.current = r)}
                        layoutName={keyboardLayoutName}
                        theme={`hg-theme-default hg-layout-default ${keyboardLayoutName === "numbers" ? 'simple-numbers-keyboard' : ''}`}
                        // enterButtonText="Next"
                        // enterButtonText={`${activeInput !== passwordInputRef.current ? 'Next >' : 'Previous'}`}
                        enterButtonText={`${activeInput !== passwordInputRef.current ? t('next') : t('previous')}`}
                        onChange={handleChangeKeyboard}
                        onKeyPress={handleKeyboardKeyPress}
                        // mergeDisplay={true}
                        // autoUseTouchEvents={true}
                        // useButtonTag={true}
                        disabled={!activeInput}
                    />
                )}
            </div>
        </>
    )
}

export default Login;
